import { useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DataTable, type DataTableColumn } from "@shop-ledger/ui-components";
import { usePlatformClient } from "../../api/ClientContext";
import { StatusBanner } from "../../components/StatusBanner";
import { useAsyncData } from "../../lib/useAsyncData";
import type { PlatformAuditLogEntry } from "../../api/types";

/** Renders a one-line human-readable summary from whichever fields this entry actually
 * carries. The mock client always sets `detail` (a single string); the real client always
 * sets `detail: null` and instead carries `before_state`/`after_state` JSONB — this
 * function is the one place that reconciles the two shapes into the same column, per this
 * app's ARCHITECTURE.md shape-difference note. */
function describeEntry(entry: PlatformAuditLogEntry): string {
  if (entry.detail) return entry.detail;
  const before = entry.before_state ? JSON.stringify(entry.before_state) : null;
  const after = entry.after_state ? JSON.stringify(entry.after_state) : null;
  if (before && after) return `${before} → ${after}`;
  if (after) return after;
  if (before) return before;
  return "—";
}

/**
 * §9.1's audit-log requirement — "every platform-admin action is written to an append-only
 * audit log" — surfaced as its own real, filterable view (not just embedded read-only on
 * the tenant detail page, since a platform-wide view is what an operator investigating an
 * incident actually needs). Genuinely live by default: `realPlatformApiClient.listAuditLog`
 * reads `GET /platform/audit-log`, a real, paginated, filterable read of the append-only
 * `platform_audit_log` table (`backend/app/models/platform.py`). The mock client models the
 * exact same shape end-to-end in an in-memory array.
 *
 * Optionally pre-filtered by `?tenant=<id>` (linked from `TenantDetailPage`'s "View audit
 * log" action).
 */
export function AuditLogPage() {
  const client = usePlatformClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tenantFilter, setTenantFilter] = useState(searchParams.get("tenant") ?? "");

  const fetchAuditLog = useCallback(
    () => client.listAuditLog(tenantFilter || undefined),
    [client, tenantFilter],
  );
  const { data, loading, error, reload } = useAsyncData(fetchAuditLog, [fetchAuditLog]);

  const columns: DataTableColumn<PlatformAuditLogEntry, string>[] = [
    { key: "timestamp", header: "Time", render: (row) => new Date(row.timestamp).toLocaleString() },
    { key: "action", header: "Action", render: (row) => <span className="badge badge--active">{row.action}</span> },
    { key: "operator_email", header: "Operator", render: (row) => row.operator_email },
    {
      key: "target_tenant_id",
      header: "Tenant",
      render: (row) => row.target_tenant_id ?? <span className="muted">—</span>,
    },
    { key: "detail", header: "Detail", render: (row) => <span className="muted">{describeEntry(row)}</span> },
  ];

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Audit Log</h1>
          <p className="page__subtitle">
            Every mutating platform-operator action (login/logout, business create/suspend/reactivate, backup
            trigger), append-only.
          </p>
        </div>
      </div>

      {error ? <StatusBanner kind="error">{error}</StatusBanner> : null}

      <div className="filter-bar panel">
        <div className="sl-field">
          <label className="sl-field__label" htmlFor="audit-tenant">
            Tenant ID
          </label>
          <input
            id="audit-tenant"
            className="sl-input"
            placeholder="filter by tenant id"
            value={tenantFilter}
            onChange={(e) => {
              const next = e.target.value;
              setTenantFilter(next);
              setSearchParams(next ? { tenant: next } : {});
            }}
          />
        </div>
        <button type="button" className="sl-button sl-button--secondary sl-button--md" onClick={reload}>
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      <div className="panel">
        <DataTable
          columns={columns}
          rows={data ?? []}
          getRowId={(row) => row.id}
          emptyMessage={loading ? "Loading…" : "No audit-log entries match this filter."}
        />
      </div>
    </div>
  );
}
